/**
 * locationService – mirrors the mobile app's LocationSearchService (location_service.dart).
 *
 * Uses the Google Maps JavaScript SDK (loaded via googleMapsLoader) so that
 * browser CORS restrictions are transparently handled.
 *
 * Key parity with mobile:
 *  - Same GOOGLE_API_KEY
 *  - Same address-component extraction logic (OSMPlace.fromGooglePlaces)
 *  - Results filtered to places that have city + state + country
 *  - 800 ms debounce applied at the UI layer (matching mobile's Timer(Duration(milliseconds: 800)))
 *  - reverseGeocode() used for "Use Current Location" (mirrors _getCurrentLocation)
 */

import { loadGoogleMapsScript } from './googleMapsLoader';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Mirrors the mobile OSMPlace model (despite the name, backed by Google Places API). */
export interface Place {
  placeId: string;
  displayName: string;
  lat: number;
  lon: number;
  houseNumber?: string;
  road?: string;
  suburb?: string;
  city?: string;
  county?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  postcode?: string;
  /** Google Place primary type (e.g. 'locality', 'route', 'establishment') */
  type: string;
  rating?: number;
}

/** Mirrors the mobile LocationBodyDto – the payload shape sent to the API. */
export interface LocationBody {
  address: string;
  city: string;
  state: string;
  country: string;
  latitude: number;
  longitude: number;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleAny = any;

/**
 * A hidden <div> is required by PlacesService as a dummy "map" container.
 * We create it once and reuse it.
 */
let _placesDiv: HTMLDivElement | null = null;

function getPlacesDiv(): HTMLDivElement {
  if (!_placesDiv) {
    _placesDiv = document.createElement('div');
    _placesDiv.style.display = 'none';
    document.body.appendChild(_placesDiv);
  }
  return _placesDiv;
}

/**
 * Parse Google address_components array into Place fields.
 * Mirrors OSMPlace.fromGooglePlaces() address extraction in the mobile app.
 */
function parseAddressComponents(components: GoogleAny[]): Partial<Place> {
  const map: Record<string, string> = {};

  for (const c of components) {
    const types: string[] = c.types ?? [];
    const longName: string = c.long_name ?? '';
    const shortName: string = c.short_name ?? '';
    for (const t of types) {
      map[t] = longName;
      if (t === 'country') map['country_code'] = shortName;
    }
  }

  // City extraction hierarchy – mirrors OSMPlace._extractCity() in mobile
  const city =
    map['locality'] ??
    map['postal_town'] ??
    map['sublocality_level_1'] ??
    map['administrative_area_level_3'] ??
    map['administrative_area_level_2'];

  return {
    houseNumber: map['street_number'],
    road: map['route'],
    suburb: map['sublocality'] ?? map['neighborhood'],
    city,
    county: map['administrative_area_level_2'],
    state: map['administrative_area_level_1'],
    country: map['country'],
    countryCode: map['country_code'],
    postcode: map['postal_code'],
  };
}

/** Convert a raw PlacesService result to a Place. */
function detailsToPlace(result: GoogleAny): Place {
  const loc = result.geometry?.location;
  const components = parseAddressComponents(result.address_components ?? []);
  return {
    placeId: result.place_id ?? '',
    displayName: result.name ?? result.formatted_address ?? '',
    lat: typeof loc?.lat === 'function' ? loc.lat() : (loc?.lat ?? 0),
    lon: typeof loc?.lng === 'function' ? loc.lng() : (loc?.lng ?? 0),
    type: (result.types ?? [])[0] ?? 'establishment',
    rating: result.rating,
    ...components,
  };
}

/** Fetch detailed info (including lat/lng + address components) for one place ID. */
async function getPlaceDetails(placeId: string): Promise<Place | null> {
  const google = (window as GoogleAny).google;
  const service = new google.maps.places.PlacesService(getPlacesDiv());

  return new Promise((resolve) => {
    service.getDetails(
      {
        placeId,
        fields: [
          'place_id',
          'name',
          'formatted_address',
          'geometry',
          'address_components',
          'types',
          'rating',
        ],
      },
      (result: GoogleAny, status: string) => {
        if (status === 'OK' && result) {
          resolve(detailsToPlace(result));
        } else {
          resolve(null);
        }
      },
    );
  });
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Search for places using Google Places Autocomplete.
 * Mirrors LocationSearchService.searchPlaces() with a limit of up to 5 predictions
 * (to keep detail-fetching round trips to a minimum on web).
 *
 * Results are optionally filtered to only include places with city + state + country populated
 * (same filter as the mobile _performSearch() method).
 */
export async function searchPlaces(
  query: string,
  limit = 5,
  requireFullAddress = true,
): Promise<Place[]> {
  await loadGoogleMapsScript();
  const google = (window as GoogleAny).google;

  const autocomplete = new google.maps.places.AutocompleteService();

  const predictions: GoogleAny[] = await new Promise((resolve, reject) => {
    autocomplete.getPlacePredictions(
      { input: query, types: ['geocode', 'establishment'] },
      (preds: GoogleAny[], status: string) => {
        if (status === 'OK' || status === 'ZERO_RESULTS') {
          resolve(preds ?? []);
        } else {
          reject(new Error(`Places Autocomplete error: ${status}`));
        }
      },
    );
  });

  // Fetch details for each prediction (up to `limit`)
  const places = await Promise.all(
    predictions.slice(0, limit).map((p) => getPlaceDetails(p.place_id)),
  );

  if (!requireFullAddress) {
    return places.filter((p): p is Place => p !== null);
  }

  // Filter: must have city + state + country (mirrors mobile _performSearch filter)
  return places.filter(
    (p): p is Place =>
      p !== null &&
      Boolean(p.city?.trim()) &&
      Boolean(p.state?.trim()) &&
      Boolean(p.country?.trim()),
  );
}

/**
 * Reverse-geocode a lat/lng to a Place.
 * Used by the "Use Current Location" feature (mirrors LocationSearchService.reverseGeocode()).
 */
export async function reverseGeocode(lat: number, lon: number): Promise<Place | null> {
  await loadGoogleMapsScript();
  const google = (window as GoogleAny).google;
  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ location: { lat, lng: lon } }, (results: GoogleAny[], status: string) => {
      if (status === 'OK' && results?.[0]) {
        const r = results[0];
        const components = parseAddressComponents(r.address_components ?? []);
        const loc = r.geometry?.location;
        resolve({
          placeId: r.place_id ?? '',
          displayName: r.formatted_address ?? '',
          lat: typeof loc?.lat === 'function' ? loc.lat() : lat,
          lon: typeof loc?.lng === 'function' ? loc.lng() : lon,
          type: 'geocode',
          ...components,
        });
      } else {
        resolve(null);
      }
    });
  });
}

/** Convert a selected Place to the LocationBody shape sent to the signup API. */
export function placeToLocationBody(place: Place): LocationBody {
  return {
    address: place.displayName,
    city: place.city ?? '',
    state: place.state ?? '',
    country: place.country ?? '',
    latitude: place.lat,
    longitude: place.lon,
  };
}

/**
 * Primary display name – city, state  (mirrors OSMPlace.primaryName in mobile).
 */
export function getPrimaryName(place: Place): string {
  if (place.city && place.state) return `${place.city}, ${place.state}`;
  if (place.city) return place.city;
  return place.displayName.split(',')[0] ?? 'Unknown location';
}

/**
 * Secondary display name – country (+ road if available)  (mirrors OSMPlace.secondaryName).
 */
export function getSecondaryName(place: Place): string {
  const parts: string[] = [];
  if (place.road) parts.push(place.road);
  if (place.country) parts.push(place.country);
  return parts.join(', ');
}
